import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Camera, Maximize2, Minimize2, Settings, X,
} from "lucide-react";
import { toast } from "sonner";
import TradingViewChart from "@/components/trade/TradingViewChart";
import DepthChart from "@/components/trade/DepthChart";
import { downloadOhlcSummaryPng, downloadSvgAsPng } from "@/components/trade/chartCapture";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";

const T = {
  surface2: "#0F151D",
  surface3: "#131A24",
  border: "#1F2937",
  text: "#E5E7EB",
  sub: "#94A3B8",
  dim: "#64748B",
  green: "#22C55E",
  red: "#EF4444",
  blueLight: "#3B82F6",
};

const INTERVALS = [
  { k: "1m", label: "1 Min" },
  { k: "5m", label: "5 Min" },
  { k: "15m", label: "15 Min" },
  { k: "30m", label: "30 Min" },
  { k: "1h", label: "1 Hour" },
  { k: "1d", label: "1 Day" },
  { k: "1w", label: "1 Week" },
  { k: "1M", label: "1 Month" },
];

const CHART_SETTINGS_KEY = "cm_trade_chart_settings";

function loadChartSettings() {
  try {
    const raw = localStorage.getItem(CHART_SETTINGS_KEY);
    if (!raw) return { showOhlc: true, defaultTab: "kline" };
    const parsed = JSON.parse(raw);
    return {
      showOhlc: parsed.showOhlc !== false,
      defaultTab: parsed.defaultTab === "depth" ? "depth" : "kline",
    };
  } catch {
    return { showOhlc: true, defaultTab: "kline" };
  }
}

function saveChartSettings(next) {
  try {
    localStorage.setItem(CHART_SETTINGS_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function fmtPrice(n, quote) {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return "—";
  if (quote === "INR") return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  if (v < 0.01) return v.toFixed(8);
  if (v < 1) return v.toFixed(6);
  if (v < 100) return v.toFixed(4);
  return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function fmtPct(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
}

function ChartPanel({
  base,
  quote,
  pair,
  interval,
  setInterval,
  ticker,
  orderBook,
  tab,
  setTab,
  showOhlc,
  onShowOhlcChange,
  expanded,
  onExpand,
  onCollapse,
  depthRef,
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);

  const bookForPair = orderBook?.pair === pair || !orderBook?.pair;
  const depthBids = bookForPair ? orderBook?.bids : [];
  const depthAsks = bookForPair ? orderBook?.asks : [];
  const o = Number(ticker?.open_24h) || 0;
  const h = Number(ticker?.high_24h) || 0;
  const l = Number(ticker?.low_24h) || 0;
  const c = Number(ticker?.last) || 0;
  const change = Number(ticker?.change_abs_24h) ?? (c - o);
  const changePct = Number(ticker?.change_pct_24h) ?? (o ? (change / o) * 100 : 0);
  const up = change >= 0;

  useEffect(() => {
    if (!settingsOpen) return undefined;
    const onDoc = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [settingsOpen]);

  async function captureChart() {
    const stamp = `${pair.replace("/", "-")}-${interval}-${Date.now()}`;
    try {
      if (tab === "depth") {
        const svg = depthRef.current?.exportSvg?.();
        if (!svg) {
          toast.error("Depth chart is not ready yet.");
          return;
        }
        await downloadSvgAsPng(svg, `coinmbx-depth-${stamp}.png`);
        toast.success("Depth chart saved.");
        return;
      }
      downloadOhlcSummaryPng({
        pair,
        interval,
        o,
        h,
        l,
        c,
        changePct,
        quote,
        fmtPrice,
        fmtPct,
      }, `coinmbx-${stamp}.png`);
      toast.success("Summary saved. For full candlesticks, use Save in the TradingView chart header.");
    } catch {
      toast.error("Could not save chart image.");
    }
  }

  return (
    <>
      <div className="flex items-center px-4 h-[40px] border-b shrink-0" style={{ borderColor: T.border }}>
        <button type="button" onClick={() => setTab("kline")} data-testid="chart-tab-kline"
          className="relative h-full px-3 text-[13px] font-bold transition"
          style={{ color: tab === "kline" ? T.blueLight : T.sub }}>
          K-Line
          {tab === "kline" && (
            <span className="absolute left-2 right-2 bottom-0 h-[2px]" style={{ background: T.blueLight }} />
          )}
        </button>
        <button type="button" onClick={() => setTab("depth")} data-testid="chart-tab-depth"
          className="relative h-full px-3 text-[13px] font-bold transition"
          style={{ color: tab === "depth" ? T.blueLight : T.sub }}>
          Depth Chart
          {tab === "depth" && (
            <span className="absolute left-2 right-2 bottom-0 h-[2px]" style={{ background: T.blueLight }} />
          )}
        </button>
        <button type="button" onClick={() => toast.info("Token Info — coming in the next release.")}
          data-testid="chart-tab-token-info"
          className="relative h-full px-3 text-[13px] font-bold transition"
          style={{ color: T.sub }}>
          Token Info
        </button>
      </div>

      {tab === "kline" && (
        <>
          <div className="flex items-center gap-1.5 px-3 h-[40px] border-b overflow-x-auto shrink-0"
            style={{ borderColor: T.border, background: T.surface2 }}>
            {INTERVALS.map((iv) => (
              <button key={iv.k} type="button" onClick={() => setInterval(iv.k)}
                data-testid={`chart-interval-${iv.k}`}
                className="px-3 h-[26px] rounded text-[11.5px] font-bold transition whitespace-nowrap"
                style={{
                  background: interval === iv.k ? T.surface3 : "transparent",
                  color: interval === iv.k ? T.blueLight : T.sub,
                  border: interval === iv.k ? `1px solid ${T.blueLight}` : "1px solid transparent",
                }}>
                {iv.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-1.5 relative" ref={settingsRef}>
              <button
                type="button"
                title="Chart settings"
                onClick={() => setSettingsOpen((v) => !v)}
                className="p-1.5 rounded hover:bg-white/5"
                data-testid="chart-tool-settings"
              >
                <Settings size={14} style={{ color: settingsOpen ? T.blueLight : T.sub }} />
              </button>
              {settingsOpen ? (
                <ChartSettingsMenu
                  showOhlc={showOhlc}
                  defaultTab={tab}
                  onChange={(patch) => {
                    const current = loadChartSettings();
                    const next = { ...current, ...patch };
                    saveChartSettings(next);
                    if (patch.defaultTab) setTab(patch.defaultTab);
                    if (typeof patch.showOhlc === "boolean") onShowOhlcChange(patch.showOhlc);
                    setSettingsOpen(false);
                    toast.success("Chart settings saved.");
                  }}
                />
              ) : null}
              {expanded ? (
                <button
                  type="button"
                  title="Exit expanded chart"
                  onClick={onCollapse}
                  className="p-1.5 rounded hover:bg-white/5"
                  data-testid="chart-tool-collapse"
                >
                  <Minimize2 size={14} style={{ color: T.sub }} />
                </button>
              ) : (
                <button
                  type="button"
                  title="Expand chart"
                  onClick={onExpand}
                  className="p-1.5 rounded hover:bg-white/5"
                  data-testid="chart-tool-fullscreen"
                >
                  <Maximize2 size={14} style={{ color: T.sub }} />
                </button>
              )}
              <button
                type="button"
                title="Save chart image"
                onClick={captureChart}
                className="p-1.5 rounded hover:bg-white/5"
                data-testid="chart-tool-screenshot"
              >
                <Camera size={14} style={{ color: T.sub }} />
              </button>
            </div>
          </div>
          {showOhlc ? (
            <div className="flex items-center gap-3 px-4 py-2 border-b font-mono text-[11.5px] shrink-0 flex-wrap"
              style={{ borderColor: T.border }} data-testid="chart-ohlc-inline">
              <span className="font-bold" style={{ color: T.text }}>{pair} · {interval}</span>
              <span style={{ color: up ? T.green : T.red }}>●</span>
              <span style={{ color: T.sub }}>O <span style={{ color: T.text }}>{fmtPrice(o, quote)}</span></span>
              <span style={{ color: T.sub }}>H <span style={{ color: T.text }}>{fmtPrice(h, quote)}</span></span>
              <span style={{ color: T.sub }}>L <span style={{ color: T.text }}>{fmtPrice(l, quote)}</span></span>
              <span style={{ color: T.sub }}>C <span style={{ color: T.text }}>{fmtPrice(c, quote)}</span></span>
              <span style={{ color: up ? T.green : T.red }} className="font-bold">
                {Number.isFinite(change) ? `${change >= 0 ? "+" : ""}${change.toFixed(2)}` : "—"}
                {" "}({fmtPct(changePct)})
              </span>
            </div>
          ) : null}
        </>
      )}

      {tab === "depth" && (
        <div className="flex items-center justify-end gap-1.5 px-3 h-[40px] border-b shrink-0"
          style={{ borderColor: T.border, background: T.surface2 }}>
          {expanded ? (
            <button type="button" title="Exit expanded chart" onClick={onCollapse}
              className="p-1.5 rounded hover:bg-white/5" data-testid="chart-tool-collapse-depth">
              <Minimize2 size={14} style={{ color: T.sub }} />
            </button>
          ) : (
            <button type="button" title="Expand chart" onClick={onExpand}
              className="p-1.5 rounded hover:bg-white/5" data-testid="chart-tool-fullscreen-depth">
              <Maximize2 size={14} style={{ color: T.sub }} />
            </button>
          )}
          <button type="button" title="Save depth chart" onClick={captureChart}
            className="p-1.5 rounded hover:bg-white/5" data-testid="chart-tool-screenshot-depth">
            <Camera size={14} style={{ color: T.sub }} />
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 relative">
        {tab === "kline" ? (
          <TradingViewChart base={base} quote={quote} interval={interval} height="100%" />
        ) : tab === "depth" ? (
          <DepthChart
            ref={depthRef}
            key={`depth-${base}-${quote}-${expanded ? "xl" : "md"}`}
            pairKey={pair}
            bids={depthBids}
            asks={depthAsks}
            quote={quote}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-[13px]" style={{ color: T.dim }}>
            Token Info coming soon.
          </div>
        )}
      </div>
    </>
  );
}

function ChartSettingsMenu({ showOhlc, defaultTab, onChange }) {
  return (
    <div
      className="absolute right-0 top-full mt-1 z-50 w-[220px] rounded-lg border py-2 shadow-xl"
      style={{ background: T.surface3, borderColor: T.border }}
      data-testid="chart-settings-menu"
    >
      <label className="flex items-center gap-2 px-3 py-2 text-[12px] cursor-pointer hover:bg-white/5"
        style={{ color: T.text }}>
        <input
          type="checkbox"
          checked={showOhlc}
          onChange={(e) => onChange({ showOhlc: e.target.checked })}
          className="rounded"
        />
        Show OHLC bar
      </label>
      <div className="px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: T.dim }}>Default tab</div>
      {["kline", "depth"].map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => onChange({ defaultTab: t })}
          className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-white/5"
          style={{ color: defaultTab === t ? T.blueLight : T.sub }}
        >
          {t === "kline" ? "K-Line" : "Depth Chart"}
        </button>
      ))}
    </div>
  );
}

function ChartExpandedOverlay(props) {
  const { pair, onClose, depthRef, ...rest } = props;
  useBodyScrollLock(true);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex flex-col"
      style={{ background: T.surface2 }}
      data-testid="chart-expanded-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Expanded chart ${pair}`}
    >
      <div className="flex items-center justify-between px-4 h-[48px] border-b shrink-0"
        style={{ borderColor: T.border, background: T.surface3 }}>
        <div className="text-[14px] font-bold" style={{ color: T.text }}>{pair} — Chart</div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-white/10 transition"
          aria-label="Close expanded chart"
          data-testid="chart-expanded-close"
        >
          <X size={18} style={{ color: T.sub }} />
        </button>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        <ChartPanel {...rest} pair={pair} expanded depthRef={depthRef} onCollapse={onClose} onExpand={undefined} />
      </div>
    </div>,
    document.body,
  );
}

export default function ChartWorkspace(props) {
  const {
    base, quote, pair, interval, setInterval, ticker, orderBook,
  } = props;

  const initial = loadChartSettings();
  const [tab, setTab] = useState(initial.defaultTab);
  const [showOhlc, setShowOhlc] = useState(initial.showOhlc);
  const [expanded, setExpanded] = useState(false);
  const depthRef = useRef(null);

  const panelProps = {
    base,
    quote,
    pair,
    interval,
    setInterval,
    ticker,
    orderBook,
    tab,
    setTab,
    showOhlc,
    onShowOhlcChange: setShowOhlc,
    depthRef,
    onExpand: () => setExpanded(true),
    onCollapse: () => setExpanded(false),
  };

  return (
    <div className="flex flex-col h-full min-h-0" style={{ background: T.surface2 }}>
      {!expanded ? (
        <ChartPanel {...panelProps} expanded={false} />
      ) : (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex items-center px-4 h-[40px] border-b shrink-0" style={{ borderColor: T.border }}>
            <span className="text-[13px] font-bold" style={{ color: T.text }}>{pair}</span>
            <span className="ml-2 text-[11px]" style={{ color: T.dim }}>Expanded view active</span>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-bold hover:bg-white/5"
              style={{ color: T.blueLight }}
              data-testid="chart-restore-inline"
            >
              <Minimize2 size={14} /> Restore chart
            </button>
          </div>
          <div className="flex-1 min-h-[120px] flex items-center justify-center text-[12px]" style={{ color: T.dim }}>
            Chart is fullscreen — press Esc or Restore to return here.
          </div>
        </div>
      )}
      {expanded ? (
        <ChartExpandedOverlay
          {...panelProps}
          expanded
          onClose={() => setExpanded(false)}
        />
      ) : null}
    </div>
  );
}

export { INTERVALS as CHART_INTERVALS };
